import React from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks';
import { MotionContainer } from './Styles';

const BlockStatus = () => {
  const {
    AuctionStore: { connected },
    BidStore: {
      auction: {
        statusBgColor,
        statusColor,
        blockStatus: originalBlockStatus,
        winningBidderName,
        winningBidderNumber,
        flashBgColor,
        flashColor,
      },
      isFloorBidderBid,
      isNotOpenForBidding,
    },
  } = useStores();

  // we don't want to see proxy on the tote for the floor bidders to see.
  const blockStatus = originalBlockStatus
    .replace('(Proxy)', '')
    .replace('- Proxy Override', '')
    .replace('Proxy', '')
    .trim();

  const getWinningBidder = () => {
    if (blockStatus?.indexOf('Currently') > -1)
      return `${winningBidderName?.toUpperCase()} (${winningBidderNumber})`;
    if (winningBidderName === 'FLOOR BIDDER')
      return winningBidderName.toUpperCase();
    return `${winningBidderName?.toUpperCase()} (${winningBidderNumber})`;
  };

  const getBlockStatusInfo = () => {
    if (!isNotOpenForBidding() && !winningBidderName)
      return {
        bgColor: statusBgColor,
        color: statusColor,
        blockStatus: 'Open for Bidding',
      };

    // lane statuses
    if (blockStatus === 'Lane Paused')
      return { bgColor: '#FBCC08', color: '#000', blockStatus };
    if (blockStatus === 'Lane Ended')
      return { bgColor: '#DB2828', color: '#fff', blockStatus };

    if (blockStatus?.indexOf('Currently') > -1)
      return {
        bgColor:
          blockStatus?.indexOf('FLOOR BIDDER') > -1 ? '#21BA45' : '#0095E8',
        color: '#fff',
        blockStatus: getWinningBidder(),
      };

    // show bidder information and status color before changing units.
    if (winningBidderName !== '')
      return { bgColor: statusBgColor, color: statusColor, blockStatus };

    // lane started, but not open for bidding
    if (isNotOpenForBidding())
      return {
        bgColor: statusBgColor,
        color: statusColor,
        blockStatus: 'Not Open for Bidding',
      };

    // if not a bid
    if (!!winningBidderName && blockStatus.indexOf(winningBidderName) < 0)
      return { bgColor: statusBgColor, color: statusColor, blockStatus };

    // if a floor or online bid
    if (isFloorBidderBid) {
      return {
        bgColor: '#21BA45',
        color: '#fff',
        flashBgColor: '#e7e7e7',
        flashColor: '#000',
        blockStatus: getWinningBidder(),
      };
    }

    if (!isFloorBidderBid) {
      return {
        bgColor: '#0095E8',
        color: '#fff',
        flashBgColor: '#e7e7e7',
        flashColor: '#000',
        blockStatus: `ONLINE BIDDER (${winningBidderName.toUpperCase()})`,
      };
    }

    // all else
    return { bgColor: statusBgColor, color: statusColor, blockStatus };
  };

  // floor tote requires different status messages, so we need to custom format
  const {
    bgColor,
    color,
    blockStatus: status,
    flashBgColor: fbgColor,
    flashColor: fColor,
  } = getBlockStatusInfo();

  const getFlashBgColor = () => {
    if (fbgColor) return fbgColor;
    return flashBgColor ?? bgColor;
  };

  const getFlashColor = () => {
    if (fColor) return fColor;
    return flashColor ?? color;
  };

  const getVariants = () => ({
    default: {
      transition: { delay: 0.1 },
      backgroundColor: bgColor,
      color,
    },
    flash: {
      transition: { duration: '1.25' },
      backgroundColor: getFlashBgColor(),
      color: getFlashColor(),
    },
  });

  return (
    <MotionContainer
      initial="flash"
      animate="default"
      exit="flash"
      variants={getVariants()}
      data-testid="block-status"
      className="blockStatus"
      key={Math.random()}
      disabled={!connected}
    >
      <h2>{status}</h2>
    </MotionContainer>
  );
};

export default observer(BlockStatus);
